// ---------------------------------------------------------------------------
// DSS-26: State Selector Tabs — Normal | Hover | Focus | Active
// ---------------------------------------------------------------------------
// Compact tab bar for the Puck sidebar. Shows which pseudo-state is being
// edited. A dot indicator appears on tabs that have overrides defined.
// ---------------------------------------------------------------------------

import { type StyleState, STYLE_STATES, type StyleStateOverrides, hasStateOverride } from "./style-state-system";

interface StateTabsProps {
  activeState: StyleState;
  onStateChange: (state: StyleState) => void;
  overrides: StyleStateOverrides;
}

const STATE_LABELS: Record<StyleState, string> = {
  normal: "Normal",
  hover: "Hover",
  focus: "Focus",
  active: "Active",
};

export default function StateTabs({
  activeState,
  onStateChange,
  overrides,
}: StateTabsProps) {
  return (
    <div
      style={{
        display: "flex",
        gap: "2px",
        padding: "2px",
        borderRadius: "8px",
        background: "var(--color-muted)",
        width: "100%",
      }}
    >
      {STYLE_STATES.map((state) => {
        const isActive = activeState === state;
        const hasOverride = hasStateOverride(overrides, state);

        return (
          <button
            key={state}
            type="button"
            onClick={() => onStateChange(state)}
            style={{
              flex: 1,
              position: "relative",
              padding: "6px 8px",
              fontSize: "11px",
              fontWeight: isActive ? 600 : 400,
              lineHeight: "1",
              color: isActive
                ? "var(--color-primary)"
                : "var(--color-muted-foreground)",
              background: isActive ? "var(--color-card)" : "transparent",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 150ms ease",
              boxShadow: isActive ? "var(--shadow-sm)" : "none",
              whiteSpace: "nowrap",
            }}
          >
            {STATE_LABELS[state]}
            {/* Override indicator dot */}
            {hasOverride && (
              <span
                style={{
                  position: "absolute",
                  top: "3px",
                  right: "4px",
                  width: "5px",
                  height: "5px",
                  borderRadius: "50%",
                  background: "var(--color-primary)",
                }}
                aria-label={`${STATE_LABELS[state]} has overrides`}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
